import RouteTemplate from 'ember-route-template'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import Component from '@glimmer/component'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { t } from 'ember-intl'
import { task } from 'ember-concurrency'
import type { BufferedChangeset } from 'ember-changeset/types'
import { ContentTile } from 'teacher-ui-client/components/tiles/content'
import { usesExternalProvisioning } from 'teacher-ui-client/helpers/uses-external-provisioning'
import { usesExternalAuthentication } from 'teacher-ui-client/helpers/uses-external-authentication'
import { FormEditTeacher } from 'teacher-ui-client/components/form/edit-teacher'
import { FormEditSchoolComponent } from 'teacher-ui-client/components/form/edit-school'
import { AccountSettings } from 'teacher-ui-client/components/account-settings'
import type SubscriptionTypeAccountRoute from 'teacher-ui-client/subscription-type/account/route'
import {
  InlineAlert,
  AlertType,
  InlineAlertSubtitle,
  InlineAlertActionsContainer,
} from 'teacher-ui-client/components/inline-alert'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import { service } from '@ember/service'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import type LogoutService from 'teacher-ui-client/services/logout'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import type ExternalRosteringService from 'teacher-ui-client/services/external-rostering'
import type SessionService from 'teacher-ui-client/services/session'
import type FeaturesService from 'teacher-ui-client/services/features'
import type DashboardPreferencesService from 'teacher-ui-client/services/dashboard-preferences'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type TeacherEvents from 'teacher-ui-client/services/teacher-events'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { and } from 'ember-truth-helpers'

interface Args {
  model: ModelFor<SubscriptionTypeAccountRoute>
}

interface Signature {
  Element: Element
  Args: Args
}

const rosterProfileUrl = join(config.oneRosterUrl, 'login').toString().concat('?return_to=/profile')
const rosterSchoolSettingsUrl = join(config.oneRosterUrl, 'login').toString().concat('?return_to=/school-settings')

export class AccountPage extends Component<Signature> {
  @service featureControl!: FeatureControlService

  @service logout!: LogoutService

  @service externalRostering!: ExternalRosteringService

  @service session!: SessionService

  @service features!: FeaturesService

  @service('dashboard-preferences') dashboardPreferences!: DashboardPreferencesService

  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  @service teacherEvents!: TeacherEvents

  @service('subscription-type') subscriptionType!: SubscriptionTypeService

  get product() {
    return String(this.subscriptionType.current)
  }

  get isOneRosterSchool() {
    return Boolean(this.session.currentSchool.oneRosterEnabledAt)
  }

  get hasDisco() {
    return Boolean(this.session.currentSchool.district?.districtCoordinators?.length)
  }

  // If school has a disco, the select is disabled and has it's own tooltip.
  get hideStandardsSetSelect() {
    return this.features.isEnabled('tui_roster_standards_setting') && this.isOneRosterSchool && !this.hasDisco
  }

  get showWondeStandaloneSchoolSettingsLink() {
    return this.externalRostering.source === 'wonde' && !this.session.currentSchool.district && this.args.model.isSubco
  }

  get showEditPasswordCta() {
    return !this.session.currentSchool.usesExternalAuthentication || this.externalRostering.source === 'wonde'
  }

  saveTeacherTask = task({ drop: true }, async (teacherChangeset: BufferedChangeset, saveOptions?: object) => {
    await teacherChangeset.save(saveOptions)
  }); /* prettier-ignore */ /* prettier-ignore-end */

  get isOneRosterEnabled() {
    return this.featureControl.canAccess('one_roster')
  }

  get showTeacherHomeSection() {
    return this.features.isEnabled('tui_teacher_home')
  }

  get teacherHomeOptedIn() {
    return this.dashboardPreferences.teacherHomeOptedIn
  }

  get disableTeacherHomeOptIn() {
    return this.teacherHomeOptedIn || this.updateTeacherHomePreference.isRunning
  }

  get disableTeacherHomeOptOut() {
    return !this.teacherHomeOptedIn || this.updateTeacherHomePreference.isRunning
  }

  get currentTeacher() {
    return this.session.currentTeacher
  }

  logoutAndRedirectToMyAccount = () => {
    void this.logout.logoutAndRedirectToReadingeggs('/parent_ui/my-account')
  }

  updateTeacherHomePreference = task({ drop: true }, async (enabled: boolean) => {
    try {
      await this.dashboardPreferences.setTeacherHomeEnabled(enabled)
      this.teacherEvents.sendEvent(enabled ? 'TeacherHome:opted_in' : 'TeacherHome:opted_out', this.product)
      this.flashQueue.addSuccess({
        subtitle: this.intl.t('pages.myAccount.settingsFlashSuccess', { name: this.currentTeacher.fullName }),
      })
    } catch {
      this.flashQueue.addFail({
        subtitle: this.intl.t('pages.myAccount.settingsFlashFail', { name: this.currentTeacher.fullName }),
      })
    }
  }); /* prettier-ignore */ /* prettier-ignore-end */

  toggleTeacherHomePreference = (enabled: boolean) => {
    void this.updateTeacherHomePreference.perform(enabled)
  }

  <template>
    <div class="grow space-y-6" ...attributes>
      <PageHeader>
        <:title>
          <h1>
            {{t "pages.myAccount.title"}}
          </h1>
        </:title>
      </PageHeader>

      <div class="mx-auto max-w-(--breakpoint-lg) space-y-6">

        {{#if (usesExternalProvisioning)}}
          <InlineAlert @type={{AlertType.Information}} class="my-6" data-test-account-alert>
            <InlineAlertSubtitle>
              {{if
                this.externalRostering.isEnabled
                (t "pages.myAccount.externalIntegrationAlert" integrationProvider=this.externalRostering.displayName)
                (t "pages.myAccount.usesExternalProvisioningAlert")
              }}
            </InlineAlertSubtitle>
            <InlineAlertActionsContainer>
              <div class="space-y-2">
                {{#if this.showWondeStandaloneSchoolSettingsLink}}
                  <div class="font-normal" data-test-wonde-standalone-standards-alert>
                    {{t "pages.myAccount.3EssentialsAlert.standardsSet"}}
                    <span>
                      <a
                        href={{rosterSchoolSettingsUrl}}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-blue-325 hover:text-blue-350 space-x-2 whitespace-nowrap"
                      >
                        <span> {{t "pages.myAccount.3EssentialsAlert.standardsLink"}}</span>
                        <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
                      </a>
                    </span>
                  </div>
                {{/if}}
                <div>
                  {{#if this.showEditPasswordCta}}
                    <span class="font-normal" data-test-account-edit-password-cta>{{t
                        "pages.myAccount.3EssentialsAlert.editPassword"
                      }}</span>
                  {{/if}}
                  <a
                    href={{rosterProfileUrl}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-325 hover:text-blue-350 space-x-2 whitespace-nowrap"
                  >
                    <span> {{t "pages.myAccount.3EssentialsAlert.link"}}</span>
                    <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
                  </a>
                </div>
              </div>
            </InlineAlertActionsContainer>
          </InlineAlert>
        {{else if this.isOneRosterEnabled}}
          <InlineAlert data-test-account-one-roster-alert @type={{AlertType.Information}}>
            <InlineAlertSubtitle>
              {{t "pages.myAccount.3EssentialsAlert.editNameEmail"}}
              <span>
                <a
                  href={{rosterProfileUrl}}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-blue-325 hover:text-blue-350 space-x-2 whitespace-nowrap"
                >
                  <span> {{t "pages.myAccount.3EssentialsAlert.link"}}</span>
                  <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
                </a>
              </span>
            </InlineAlertSubtitle>
            {{#if (and this.hideStandardsSetSelect @model.isSubco)}}
              <InlineAlertSubtitle data-test-standards-set-roster-alert>
                {{t "pages.myAccount.3EssentialsAlert.standardsSet"}}
                <span>
                  <a
                    href={{rosterSchoolSettingsUrl}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-blue-325 hover:text-blue-350 space-x-2 whitespace-nowrap"
                  >
                    <span> {{t "pages.myAccount.3EssentialsAlert.standardsLink"}}</span>
                    <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
                  </a>
                </span>
              </InlineAlertSubtitle>
            {{/if}}
          </InlineAlert>
        {{/if}}

        {{#if @model.isHomeschool}}
          <InlineAlert data-test-account-edit-account-via-pui-alert @type={{AlertType.Information}}>
            <InlineAlertSubtitle>
              {{t "pages.myAccount.editParentAccountViaPui.text"}}
            </InlineAlertSubtitle>
            <InlineAlertActionsContainer>
              <button
                type="button"
                class="text-blue-325 hover:text-blue-350 space-x-2 whitespace-nowrap"
                data-test-redirect-to-pui-my-account
                {{on "click" this.logoutAndRedirectToMyAccount}}
              >
                <span> {{t "pages.myAccount.editParentAccountViaPui.link"}}</span>
                <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
              </button>
            </InlineAlertActionsContainer>

          </InlineAlert>

        {{else}}
          <ContentTile class="p-4">
            <FormEditTeacher
              data-test-account-edit-teacher
              @teacher={{@model.currentTeacher}}
              @saveTeacherTask={{this.saveTeacherTask}}
              @subscriptionType={{@model.subscriptionType}}
              @usesExternalAuthentication={{(usesExternalAuthentication)}}
              @usesExternalProvisioning={{(usesExternalProvisioning)}}
              @isOneRosterEnabled={{this.isOneRosterEnabled}}
            />
          </ContentTile>
        {{/if}}

        {{#if @model.isSubco}}
          <ContentTile class="p-4">
            <FormEditSchoolComponent
              data-test-account-edit-school
              @subscriptionType={{@model.subscriptionType}}
              @isHomeschool={{@model.isHomeschool}}
              @hideStandardsSetSelect={{this.hideStandardsSetSelect}}
            />
          </ContentTile>
        {{/if}}

        {{#unless @model.isHomeschool}}
          <ContentTile class="p-4">
            <AccountSettings
              data-test-account-settings
              @subscriptionType={{@model.subscriptionType}}
              @usesExternalAuthentication={{(usesExternalAuthentication)}}
              @isRolloverInProgress={{@model.isRolloverInProgress}}
            />
          </ContentTile>
        {{/unless}}

        {{#if this.showTeacherHomeSection}}
          <ContentTile class="p-4">
            <div class="flex flex-col items-start space-y-4" data-test-account-teacher-home-settings>
              <div class="font-semibold">{{t "pages.myAccount.teacherHome.title"}}</div>
              <p class="text-dusty-black-300 text-sm">
                {{t "pages.myAccount.teacherHome.description"}}
              </p>
              <div class="flex flex-wrap gap-3">
                <UiButton
                  data-test-account-teacher-home-opt-in
                  @appearance={{ButtonAppearance.regular}}
                  disabled={{this.disableTeacherHomeOptIn}}
                  {{on "click" (fn this.toggleTeacherHomePreference true)}}
                >
                  {{t "pages.myAccount.teacherHome.optInLabel"}}
                </UiButton>
                <UiButton
                  data-test-account-teacher-home-opt-out
                  @appearance={{ButtonAppearance.red}}
                  disabled={{this.disableTeacherHomeOptOut}}
                  {{on "click" (fn this.toggleTeacherHomePreference false)}}
                >
                  {{t "pages.myAccount.teacherHome.optOutLabel"}}
                </UiButton>
              </div>
            </div>
          </ContentTile>
        {{/if}}
      </div>
    </div>
  </template>
}

export default RouteTemplate(AccountPage)
